import { ModalRef } from '@msdk/common'
import { Accordion, AccordionDetails, AccordionSummary, styled, Typography, uiTokens } from '@msdk/ui'
import { Container, Dialog, DialogContent, ListItem } from '@mui/material'

export interface ModalStepProps {
  modalRef?: ModalRef
}

export const StyledDialog = styled(Dialog)<{ $width?: number }>`
  .MuiPaper-root {
    max-width: ${({ $width }) => ($width ? `${$width}px` : '503px')};
    border-radius: ${uiTokens.radius.xxLarge}px;
    border: ${uiTokens.sizing.stroke.s}px solid ${uiTokens.uiStroke.neutral};
    background: ${uiTokens.uiSurface.overlay};
    box-shadow: ${uiTokens.elevation.x[0]} ${uiTokens.elevation.y[500]}px ${uiTokens.elevation.blur[500]}px
      ${uiTokens.elevation.spread[500]}px ${uiTokens.uiSurface.effect.shadow};
  }
`

export const StyledMessageTypography = styled(Typography)`
  font-weight: ${uiTokens.font.fontWeight.regular};
  font-size: ${uiTokens.font.fontSize.body.large}px;
  line-height: ${uiTokens.font.lineHeight.body.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
  color: ${uiTokens.uiContent.neutral};
`

export const StyledEditContent = styled(DialogContent)`
  min-width: 475px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${uiTokens.spacing.gap.medium}px;
  align-self: stretch;
  border-top: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
  border-bottom: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
  padding: ${uiTokens.spacing.padding.medium}px;
  .MuiDialogTitle-root + & {
    padding-top: ${uiTokens.spacing.padding.medium}px;
  }
  background-color: ${uiTokens.uiControl.outlined.enabled};
`

export const StyledContent = styled(DialogContent)`
  min-width: 475px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${uiTokens.spacing.gap.medium}px;
  align-self: stretch;
  border-top: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
  border-bottom: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
  padding: ${uiTokens.spacing.padding.medium}px;
  .MuiDialogTitle-root + & {
    padding-top: ${uiTokens.spacing.padding.medium}px;
  }
`

export const StyledList = styled('ul')`
  margin: 0;
`

export const StyledContainer = styled(Container)`
  padding: 0;
  && {
    padding: 0;
  }
`

export const StyledListItem = styled(ListItem)`
  color: ${uiTokens.uiContent.emphasized};
  font-size: ${uiTokens.font.fontSize.uiControlText.medium}px;
  line-height: ${uiTokens.font.lineHeight.uiControlText.medium}px;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
`

export const StyledListElement = styled('li')<{ $satisfied?: boolean; $notSatisfied?: boolean }>`
  padding: 0 ${uiTokens.spacing.padding.medium}px ${uiTokens.spacing.padding.small}px
    ${uiTokens.spacing.padding.small}px;
  color: ${uiTokens.uiContent.neutral};
  font-size: ${uiTokens.font.fontSize.uiControlText.medium}px;
  font-weight: ${uiTokens.font.fontWeight.medium};
  line-height: ${uiTokens.font.lineHeight.uiControlText.medium}px;
  ${({ $satisfied, $notSatisfied }) => {
    if ($satisfied)
      return `
    list-style: none;
    position: relative;
    color: ${uiTokens.uiContent.success};
  `
    if ($notSatisfied) return `color: ${uiTokens.uiContent.error};`
    return ''
  }}
`

export const StyledAccordion = styled(Accordion)`
  &.MuiPaper-root {
    gap: ${uiTokens.spacing.gap.semiMedium}px;
    padding: ${uiTokens.spacing.padding.semiMedium}px;
    border: none;
    box-shadow: none;
    border-radius: 0;
    width: 100%;
    background-color: ${uiTokens.uiControl.outlined.enabled};
  }
  &::before {
    display: none;
  }
`

export const StyledSummaryTypography = styled(Typography)`
  color: ${uiTokens.uiContent.accent};
  font-family: ${uiTokens.font.fontFamily.default};
  font-size: ${uiTokens.font.fontSize.body.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.regular};
  line-height: ${uiTokens.font.lineHeight.body.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.mediumLoose}px;
`

export const StyledAccordionDetails = styled(AccordionDetails)`
  display: flex;
  min-height: ${uiTokens.sizing.minInteractiveTarget}px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: ${uiTokens.spacing.gap.small}px;
  &.MuiAccordionDetails-root {
    padding: 0;
  }
`

export const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0;
`
